import React, { useState, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiPlus, FiMinus, FiShoppingCart, FiHeart, FiFilter, FiGrid, FiList, FiStar } from 'react-icons/fi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import { useWholesaleCart } from '../../contexts/WholesaleCartContext';
import styles from './ProductCatalog.module.css';

const ProductDetailModal = ({ product, onClose }) => {
  if (!product) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{product.name}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <img src={product.imageUrl || 'https://via.placeholder.com/300'} alt={product.name} className={styles.modalImage} />
          <div className={styles.modalInfo}>
            <p><strong>Origin:</strong> {product.origin || 'N/A'}</p>
            <p><strong>Process:</strong> {product.process || 'N/A'}</p>
            <p><strong>Category:</strong> {product.category === 'Both' ? 'Filter/Espresso' : product.category}</p>
            <p className={styles.modalDescription}>{product.description || 'No additional details available.'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ product, onViewDetails, viewMode, isDemoAccount }) => {
  const { addItem } = useWholesaleCart();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState('whole-bean');
  
  const isOutOfStock = product.inStock === false;
  const isLowStock = product.stock && product.stock < 10;
  const rating = product.rating || 4.5;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (isOutOfStock) return;

    setIsAdding(true);
    
    const productWithVariant = {
      ...product,
      variant: selectedVariant,
      variantLabel: selectedVariant === 'whole-bean' ? 'Whole Bean' : 'Ground'
    };
    
    await addItem(productWithVariant, quantity);
    
    setTimeout(() => {
      setIsAdding(false);
    }, 1000);
  };

  const toggleWishlist = (e) => {
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  if (viewMode === 'list') {
    return (
      <div className={`${styles.productCardList} ${isOutOfStock ? styles.outOfStock : ''}`}>
        <div className={styles.listImageContainer}>
          <img src={product.imageUrl || 'https://via.placeholder.com/120'} alt={product.name} className={styles.listProductImage} />
          {isOutOfStock && <div className={styles.stockOverlay}>Out of Stock</div>}
          {isLowStock && !isOutOfStock && <div className={styles.lowStockBadge}>Low Stock</div>}
        </div>
        <div className={styles.listContent}>
          <div className={styles.listLeft}>
            <div className={styles.productCategory}>{product.category === 'Both' ? 'Filter/Espresso' : product.category}</div>
            <div className={styles.productName}>{product.name}</div>
            <div className={styles.productDescription}>{product.description || `Premium ${product.origin} coffee with exceptional flavor profile`}</div>
            <div className={styles.productMeta}>
              <span className={styles.origin}>Origin: {product.origin || 'Various'}</span>
              <span className={styles.process}>Process: {product.process || 'Washed'}</span>
            </div>
          </div>
          <div className={styles.listCenter}>
          </div>
          <div className={styles.listRight}>
            <div className={styles.priceContainer}>
              <div className={styles.productPrice}>QAR {Number(product.wholesalePrice).toFixed(2)}</div>
              <div className={styles.priceUnit}>per {product.unit || 'kg'}</div>
            </div>
            <div className={styles.stockStatus}>
              {isOutOfStock ? (
                <span className={styles.outOfStockText}>Out of Stock</span>
              ) : isLowStock ? (
                <span className={styles.lowStockText}>Only {product.stock} left</span>
              ) : (
                <span className={styles.inStockText}>In Stock</span>
              )}
            </div>
            <div className={styles.listActions}>
              <div className={styles.quantitySelector} onClick={e => e.stopPropagation()}>
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}><FiMinus /></button>
                <input type="number" value={quantity} onChange={e => setQuantity(parseInt(e.target.value, 10) || 1)} min="1" />
                <button onClick={() => setQuantity(q => q + 1)}><FiPlus /></button>
              </div>
              <button className={styles.addToCartButton} onClick={handleAddToCart} disabled={isAdding || isOutOfStock || isDemoAccount}>
                {isDemoAccount ? 'Demo - View Only' : isAdding ? 'Added' : isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.productCard} ${isOutOfStock ? styles.outOfStock : ''}`} onClick={() => onViewDetails(product)}>
      <div className={styles.cardImageContainer}>
        <img src={product.imageUrl || 'https://via.placeholder.com/300'} alt={product.name} className={styles.productImage} />
        <button 
          className={`${styles.wishlistBtn} ${styles.wishlistOverlay} ${isWishlisted ? styles.wishlisted : ''}`}
          onClick={toggleWishlist}
        >
          <FiHeart />
        </button>
        {isOutOfStock && <div className={styles.stockOverlay}>Out of Stock</div>}
        {isLowStock && !isOutOfStock && <div className={styles.lowStockBadge}>Low Stock</div>}
      </div>
      <div className={styles.cardContent}>
        <div className={styles.productCategory}>{product.category === 'Both' ? 'Filter/Espresso' : product.category}</div>
        <div className={styles.productName}>{product.name}</div>
        <div className={styles.productDescription}>{product.description || `Premium ${product.origin} coffee`}</div>
        <div className={styles.ratingContainer}>
          <div className={styles.stars}>
            {[...Array(5)].map((_, i) => (
              <FiStar key={i} className={i < Math.floor(rating) ? styles.starFilled : styles.starEmpty} />
            ))}
          </div>
          <span className={styles.ratingText}>({rating})</span>
        </div>
        <div className={styles.variants}>
          <button 
            className={`${styles.variantBtn} ${selectedVariant === 'whole-bean' ? styles.active : ''}`}
            onClick={(e) => { e.stopPropagation(); setSelectedVariant('whole-bean'); }}
          >
            Whole Bean
          </button>
          <button 
            className={`${styles.variantBtn} ${selectedVariant === 'ground' ? styles.active : ''}`}
            onClick={(e) => { e.stopPropagation(); setSelectedVariant('ground'); }}
          >
            Ground
          </button>
        </div>
        <div className={styles.priceContainer}>
          <div className={styles.productPrice}>QAR {Number(product.wholesalePrice).toFixed(2)}</div>
          <div className={styles.priceUnit}>per {product.unit || 'kg'}</div>
        </div>
        <div className={styles.stockStatus}>
          {isOutOfStock ? (
            <span className={styles.outOfStockText}>Out of Stock</span>
          ) : isLowStock ? (
            <span className={styles.lowStockText}>Only {product.stock} left</span>
          ) : (
            <span className={styles.inStockText}>In Stock</span>
          )}
        </div>
        <div className={styles.cardActions}>
          <div className={styles.quantitySelector} onClick={e => e.stopPropagation()}>
            <button onClick={() => setQuantity(q => Math.max(1, q - 1))}><FiMinus /></button>
            <input type="number" value={quantity} onChange={e => setQuantity(parseInt(e.target.value, 10) || 1)} min="1" />
            <button onClick={() => setQuantity(q => q + 1)}><FiPlus /></button>
          </div>
          <button className={styles.addToCartButton} onClick={handleAddToCart} disabled={isAdding || isOutOfStock || isDemoAccount}>
            {isDemoAccount ? 'Demo - View Only' : isAdding ? 'Added!' : isOutOfStock ? 'Out of Stock' : (
              <>
                <FiShoppingCart />
                <span>Add to Cart</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterSidebar = ({ filters, onFilterChange, onResetFilters }) => {
  return (
    <div className={styles.filterSidebar}>
      <div className={styles.filterHeader}>
        <h3>Filters</h3>
        <button onClick={onResetFilters} className={styles.resetBtn}>Reset All</button>
      </div>
      
      <div className={styles.filterGroup}>
        <h4>Coffee Type</h4>
        <div className={styles.filterOptions}>
          {['All', 'Espresso', 'Filter', 'Both'].map(type => (
            <label key={type} className={styles.filterOption}>
              <input 
                type="radio" 
                name="coffeeType" 
                value={type}
                checked={filters.coffeeType === type}
                onChange={(e) => onFilterChange('coffeeType', e.target.value)}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <h4>Price Range (QAR)</h4>
        <div className={styles.priceRange}>
          <input 
            type="range" 
            min="0" 
            max="500" 
            value={filters.priceMax} 
            onChange={(e) => onFilterChange('priceMax', e.target.value)}
            className={styles.priceSlider}
          />
          <div className={styles.priceLabels}>
            <span>0</span>
            <span>{filters.priceMax}</span>
          </div>
        </div>
      </div>

      <div className={styles.filterGroup}>
        <h4>Bag Size</h4>
        <div className={styles.filterOptions}>
          {['250g', '500g', '1kg', '5kg'].map(size => (
            <label key={size} className={styles.filterOption}>
              <input 
                type="checkbox" 
                checked={filters.bagSizes.includes(size)}
                onChange={(e) => {
                  const sizes = e.target.checked 
                    ? [...filters.bagSizes, size]
                    : filters.bagSizes.filter(s => s !== size);
                  onFilterChange('bagSizes', sizes);
                }}
              />
              <span>{size}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <h4>Origin</h4>
        <div className={styles.filterOptions}>
          {['Ethiopia', 'Colombia', 'Brazil', 'Guatemala', 'Kenya', 'Yemen'].map(origin => (
            <label key={origin} className={styles.filterOption}>
              <input 
                type="checkbox" 
                checked={filters.origins.includes(origin)}
                onChange={(e) => {
                  const origins = e.target.checked 
                    ? [...filters.origins, origin]
                    : filters.origins.filter(o => o !== origin);
                  onFilterChange('origins', origins);
                }}
              />
              <span>{origin}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <h4>Stock Status</h4>
        <div className={styles.filterOptions}>
          <label className={styles.filterOption}>
            <input 
              type="checkbox" 
              checked={filters.inStockOnly}
              onChange={(e) => onFilterChange('inStockOnly', e.target.checked)}
            />
            <span>In Stock Only</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const WholesaleProductCatalog = () => {
  const { currentWholesaleUser, wholesaleCustomer } = useWholesaleAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  
  const isDemoAccount = wholesaleCustomer?.status === 'demo';
  const [viewMode, setViewMode] = useState('list');
  const [showFilters, setShowFilters] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setShowFilters(false);
      } else {
        setShowFilters(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedProductForDetail, setSelectedProductForDetail] = useState(null);
  const [filters, setFilters] = useState({
    coffeeType: 'All',
    priceMax: 500,
    bagSizes: [],
    origins: [],
    inStockOnly: false
  });

  useEffect(() => {
    if (!wholesaleCustomer) return;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Get the customer's branch - default to 'main' if not specified
        const customerBranch = wholesaleCustomer.branch || 'main';
        console.log('Fetching products for branch:', customerBranch);
        
        const itemsPath = `wholesaleBranches/${customerBranch}/items`;
        const snapshot = await getDocs(collection(db, itemsPath));
        if (snapshot.empty) {
          setError('No products have been assigned to your branch yet. Please contact your account manager.');
          setProducts([]);
        } else {
          const productList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('Fetched products from branch:', customerBranch, productList);
          setProducts(productList);
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching wholesale products: ", err);
        setError('Could not load your product catalog. Please try again later.');
      }
      setLoading(false);
    };

    fetchProducts();
  }, [wholesaleCustomer]);

  // Handle URL hash to open product detail modal
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && products.length > 0) {
        const product = products.find(p => p.id === hash);
        if (product) {
          setSelectedProductForDetail(product);
          setDetailModalOpen(true);
        }
      }
    };

    // Check hash on mount and when products change
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [products]);

  const handleViewDetails = (product) => {
    setSelectedProductForDetail(product);
    setDetailModalOpen(true);
    // Update URL hash when opening modal
    window.history.replaceState(null, null, `#${product.id}`);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedProductForDetail(null);
    // Clear the hash when closing modal
    if (window.location.hash) {
      window.history.replaceState(null, null, window.location.pathname);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      coffeeType: 'All',
      priceMax: 500,
      bagSizes: [],
      origins: [],
      inStockOnly: false
    });
  };

  const filteredProducts = products.filter(product => {
    // Search term filter
    if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Coffee type filter
    if (filters.coffeeType !== 'All') {
      if (filters.coffeeType === 'Both') {
        if (product.category !== 'Both') return false;
      } else {
        if (product.category !== filters.coffeeType && product.category !== 'Both') return false;
      }
    }

    // Price filter
    if (product.wholesalePrice > filters.priceMax) {
      return false;
    }

    // Origin filter
    if (filters.origins.length > 0 && !filters.origins.includes(product.origin)) {
      return false;
    }

    // Stock filter
    if (filters.inStockOnly && product.inStock === false) {
      return false;
    }

    return true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return (a.wholesalePrice || 0) - (b.wholesalePrice || 0);
      case 'price-high':
        return (b.wholesalePrice || 0) - (a.wholesalePrice || 0);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'newest':
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  return (
    <div className={styles.catalogLayout}>
      <div className={styles.catalogMain}>
        <div className={styles.catalogHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerTitle}>Product Catalog</div>
            <div className={styles.headerSubtitle}>Browse and order from your selection of premium coffees</div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.resultsCount}>
              {sortedProducts.length} {sortedProducts.length === 1 ? 'product' : 'products'} found
            </div>
          </div>
        </div>

        <div className={styles.toolbarContainer}>
          <div className={styles.toolbarLeft}>
            <div className={styles.searchWrapper}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search products..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className={styles.toolbarRight}>
            <div className={styles.selectWrapper}>
              <select
                className={styles.sortSelect}
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
              </select>
              <FiChevronDown className={styles.selectIcon} />
            </div>
          </div>
        </div>

        {/* Inline Filters */}
        <div className={styles.filtersContainer}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Coffee Type:</label>
              <select 
                className={styles.filterSelect}
                value={filters.coffeeType}
                onChange={(e) => handleFilterChange('coffeeType', e.target.value)}
              >
                <option value="All">All</option>
                <option value="Espresso">Espresso</option>
                <option value="Filter">Filter</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Max Price:</label>
              <input 
                type="range" 
                min="0" 
                max="500" 
                value={filters.priceMax} 
                onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                className={styles.priceSlider}
              />
              <span className={styles.priceValue}>QAR {filters.priceMax}</span>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <input 
                  type="checkbox" 
                  checked={filters.inStockOnly}
                  onChange={(e) => handleFilterChange('inStockOnly', e.target.checked)}
                />
                In Stock Only
              </label>
            </div>

            <button onClick={handleResetFilters} className={styles.resetFiltersBtn}>
              Reset Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className={styles.messageCenter}>Loading your products...</div>
        ) : error ? (
          <div className={`${styles.messageCenter} ${styles.error}`}>{error}</div>
        ) : (
          <div className={styles.productsContainer}>
            {sortedProducts.length > 0 ? (
              <div className={styles.productList}>
                {sortedProducts.map(product => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    onViewDetails={handleViewDetails}
                    viewMode="list"
                    isDemoAccount={isDemoAccount}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.noResults}>
                <div className={styles.noResultsIcon}>🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your search criteria or filters</p>
                <button onClick={handleResetFilters} className={styles.resetFiltersBtn}>
                  Reset All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {isDetailModalOpen && <ProductDetailModal product={selectedProductForDetail} onClose={handleCloseDetailModal} />}
    </div>
  );
};

export default WholesaleProductCatalog;
